<template lang="pug">

    .field(v-bind:class='{ required: isRequired, error: $slots.error }')

        label(v-bind:for='fieldId') {{ label }}

        select.ui.multiple.search.selection.dropdown(
            ref='dropdownEl'
            v-bind:id='fieldId'
            v-bind:disabled='isDisabled'
            multiple=''
        )
            option(
                v-for='option in options'
                v-bind:key='option[optionValue]'
                v-bind:value='option[optionValue]'
            ) {{ option[optionLabel] }}

        slot

</template>

<script>

    //noinspection ReservedWordAsName,JSUnusedGlobalSymbols
    export default {

        data() {
            return {
                fieldId: this.$rndId(),
                syncing: false,
                lastEmitted: null
            }
        },

        mounted() {
            this._initDropdown()
        },

        watch: {
            value(newVal) {
                // Keep our local tracking in sync when the parent changes the
                // value externally (changes via our own onChange already match).
                if (!this._sameValues(newVal, this.lastEmitted)) {
                    this.lastEmitted = this._copy(newVal)
                }
                this._syncDropdown()
            },
            isDisabled() {
                this._syncDisabled()
            }
        },

        methods: {
            _initDropdown() {
                const el = this.$refs.dropdownEl
                if (!el) return
                // Fomantic wraps the <select> in a '.ui.dropdown' container div and
                // stores the module instance there — not on the <select> itself.
                const $container = $(el).parent('.ui.dropdown')
                if ($container.length && $container.data('module-dropdown')) return
                // Track the value we emitted last. The guard below must not rely
                // on the `value` prop, which can be stale while Vue batches updates.
                this.lastEmitted = this._copy(this.value)
                $(el).dropdown({
                    placeholder: this.placeholder,
                    onChange: (values) => {
                        // Ignore callbacks triggered by our own programmatic sync
                        if (this.syncing) return
                        const next = this._toModelValues(values)
                        if (this._sameValues(next, this.lastEmitted)) return
                        this.lastEmitted = next
                        this.$emit('input', next)
                    }
                })
                // Fomantic observes the <select> for mutations and, on every
                // selection change, rebuilds the menu and re-fires onChange with
                // intermediate values. For static option lists this breaks
                // multi-select, so disconnect the observer when not needed.
                if (!this.observeChanges) {
                    const $dd = $(el).parent('.ui.dropdown')
                    const instance = $dd.data('module-dropdown')
                    if (instance && instance.disconnect && typeof instance.disconnect.selectObserver === 'function') {
                        instance.disconnect.selectObserver()
                    }
                }
                this._syncDropdown()
            },

            _syncDropdown() {
                const el = this.$refs.dropdownEl
                if (!el) return
                const $dd = $(el).parent('.ui.dropdown')
                if (!$dd.length || !$dd.data('module-dropdown')) return
                const next = this._stringify(this.value)
                const current = this._stringify($dd.dropdown('get value'))
                if (this._sameValues(next, current)) return
                this.syncing = true
                try {
                    // Clear first, then select: Fomantic's `set selected` toggles
                    // already-selected items (and re-fires onChange) for multiple
                    // dropdowns with labels, so clear + select acts as "set exactly".
                    $dd.dropdown('clear', true)
                    if (next.length) {
                        $dd.dropdown('set selected', next, null, true)
                    }
                } finally {
                    this.syncing = false
                }
            },

            _syncDisabled() {
                const el = this.$refs.dropdownEl
                if (!el) return
                const $dd = $(el).parent('.ui.dropdown')
                if ($dd.length) $dd.toggleClass('disabled', this.isDisabled)
            },

            _copy(values) {
                return Array.isArray(values) ? values.slice() : values
            },

            _stringify(values) {
                return (Array.isArray(values) ? values : []).map(v => String(v))
            },

            _toModelValues(values) {
                const vals = Array.isArray(values) ? values : []
                return vals.map(v => {
                    const opt = this.options.find(o => String(o[this.optionValue]) === String(v))
                    return opt ? opt[this.optionValue] : v
                })
            },

            _sameValues(a, b) {
                const sa = this._stringify(a).slice().sort()
                const sb = this._stringify(b).slice().sort()
                return sa.length === sb.length && sa.every((v, i) => v === sb[i])
            }
        },

        props: {
            value: {
                required: true
            },
            label: {
                type: String,
                required: true
            },
            isDisabled: {
                type: Boolean,
                default: false
            },
            isRequired: {
                type: Boolean,
                default: false
            },
            placeholder: {
                type: String,
                default: ''
            },
            options: {
                type: Array,
                required: true
            },
            observeChanges: {
                type: Boolean,
                default: true
            },
            optionValue: {
                type: String,
                default: 'id'
            },
            optionLabel: {
                type: String,
                default: 'name'
            }
        }

    }

</script>
